/**
 * One label map for every OMS screen (OMS-CLOUD-009 / OMS-TS-111).
 *
 * Desktop stores ORDER_STATUS and eCheckStatus. Screens show these words and never a blank.
 * Q-CODE-15: Unsubmitted is "In preview", Submitted is "Released". Draft stays the unsaved form.
 * Q-CODE-08: Error and NotPerformed are Block, labelled "Check failed" and "Not checked".
 * Pending is "Received": HFOMS sets it from the broker's FIX report until the Trades Importer books the fills.
 * Order type and time in force follow eORDER_TYPE and eORDER_TIME_IN_FORCE. FAK is "IOC (fill and kill)", label still to agree.
 */
import type { OmsCheck } from './interfaces/oms';

export const OMS_UNKNOWN_LABEL = 'Unknown';

export const OMS_RECEIVED_HINT = 'The broker has the order. Its fills are not booked by the Trades Importer yet.';

/** ORDER_STATUS name to the label every screen shows. Ordinals follow this order. */
export const OMS_ORDER_STATUS_LABELS = {
  Unsubmitted: 'In preview',
  Submitted: 'Released',
  PartiallyFilled: 'Working',
  Filled: 'Complete',
  Cancelled: 'Cancelled',
  Expired: 'Expired',
  Void: 'Void',
  Pending: 'Received',
  Rejected: 'Rejected',
} as const;

export type OmsOrderStatusName = keyof typeof OMS_ORDER_STATUS_LABELS;

const ORDER_STATUS_BY_ORDINAL: readonly string[] = [
  OMS_ORDER_STATUS_LABELS.Unsubmitted,
  OMS_ORDER_STATUS_LABELS.Submitted,
  OMS_ORDER_STATUS_LABELS.PartiallyFilled,
  OMS_ORDER_STATUS_LABELS.Filled,
  OMS_ORDER_STATUS_LABELS.Cancelled,
  OMS_ORDER_STATUS_LABELS.Expired,
  OMS_ORDER_STATUS_LABELS.Void,
  OMS_ORDER_STATUS_LABELS.Pending,
  OMS_ORDER_STATUS_LABELS.Rejected,
];

const ORDER_STATUS_BY_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(OMS_ORDER_STATUS_LABELS).map(([name, label]) => [name.toLowerCase(), label]),
);

export type OmsCheckCode = Exclude<OmsCheck, 'NOT_CHECKED'>;

export type OmsCheckMapping = {
  result: OmsCheckCode | null;
  label: string;
};

const CHECK_RESULTS: Record<string, OmsCheckMapping> = {
  passed: { result: 'PASS', label: 'Pass' },
  green: { result: 'PASS', label: 'Pass' },
  ok: { result: 'PASS', label: 'Pass' },
  alert: { result: 'WARN', label: 'Warn' },
  yellow: { result: 'WARN', label: 'Warn' },
  warning: { result: 'WARN', label: 'Warn' },
  breach: { result: 'BLOCK', label: 'Block' },
  red: { result: 'BLOCK', label: 'Block' },
  failure: { result: 'BLOCK', label: 'Block' },
  error: { result: 'BLOCK', label: 'Check failed' },
  notperformed: { result: 'BLOCK', label: 'Not checked' },
  na: { result: 'BLOCK', label: 'Not checked' },
};

function codeKey(value: string): string {
  const trimmed = value.trim();
  const leaf = trimmed.includes('.') ? trimmed.slice(trimmed.lastIndexOf('.') + 1) : trimmed;
  return leaf.toLowerCase().replace(/[\s_]+/g, '');
}

function labelForOrdinal(ordinal: number): string {
  if (!Number.isInteger(ordinal) || ordinal < 0) return OMS_UNKNOWN_LABEL;
  return ORDER_STATUS_BY_ORDINAL[ordinal] ?? OMS_UNKNOWN_LABEL;
}

/** Display label for an on-prem order status name, description, or ordinal. Unknown codes stay visible. */
export function omsOrderStatusLabel(status: string | number | null | undefined): string {
  if (typeof status === 'number') return labelForOrdinal(status);
  if (typeof status !== 'string') return OMS_UNKNOWN_LABEL;
  const trimmed = status.trim();
  if (/^\d+$/.test(trimmed)) return labelForOrdinal(Number(trimmed));
  return ORDER_STATUS_BY_KEY[codeKey(trimmed)] ?? OMS_UNKNOWN_LABEL;
}

/** eORDER_TYPE name to its label. Ordinals follow this order. */
export const OMS_ORDER_TYPE_LABELS = {
  MARKET: 'Market',
  LIMIT: 'Limit',
  STOP: 'Stop',
  STOP_LIMIT: 'Stop-Limit',
  TRAILING_STOP: 'Trailing stop',
} as const;

/** eORDER_TIME_IN_FORCE name to its label. Ordinals follow this order. */
export const OMS_TIME_IN_FORCE_LABELS = {
  DAY: 'Day',
  GTC: 'GTC',
  GTD: 'GTD',
  FOK: 'FOK',
  FAK: 'IOC (fill and kill)',
  OnTheOpen: 'On the open',
  OnTheClose: 'On the close',
} as const;

function enumLabel(labels: Record<string, string>, code: string | null | undefined): string | null {
  if (typeof code !== 'string') return null;
  const trimmed = code.trim();
  const ordered = Object.values(labels);
  if (/^\d+$/.test(trimmed)) return ordered[Number(trimmed)] ?? null;
  const key = codeKey(trimmed);
  if (!key) return null;
  const match = Object.entries(labels).find(([name]) => codeKey(name) === key);
  return match ? match[1] : null;
}

/** Order type label for an on-prem eORDER_TYPE name or ordinal. Null when unknown. */
export function omsOrderTypeLabel(code: string | null | undefined): string | null {
  return enumLabel(OMS_ORDER_TYPE_LABELS, code);
}

/** Time in force label for an on-prem eORDER_TIME_IN_FORCE name or ordinal. Null when unknown. */
export function omsTimeInForceLabel(code: string | null | undefined): string | null {
  return enumLabel(OMS_TIME_IN_FORCE_LABELS, code);
}

/** Pass, Warn, or Block for an on-prem check code. Error and NotPerformed stay Block, with their own words. */
export function omsCheckLabel(code: string | null | undefined): OmsCheckMapping {
  if (typeof code !== 'string') return { result: null, label: OMS_UNKNOWN_LABEL };
  const key = codeKey(code);
  if (!key) return { result: null, label: OMS_UNKNOWN_LABEL };
  return CHECK_RESULTS[key] ?? { result: null, label: OMS_UNKNOWN_LABEL };
}
